/**
 * Palette roles (ADR 0002). Renderer code and assets name colour **roles**, never values.
 * A bundle declares its roles, a default palette and named presets; the project stores
 * an override per bundle, edited in the graphics admin during Prepare and refused while
 * OBS is streaming, recording or disconnected.
 *
 * Colours are `#rrggbb` or `#rrggbbaa`: one format the admin colour input, CSS and
 * PixiJS tints all read, with alpha for the DOM renderer's translucent panels.
 */
import { z } from 'zod';

/** Renderers that draw bundles (ADR 0002). */
export const RENDERERS = ['dom', 'signal'] as const;
export const RendererSchema = z.enum(RENDERERS);
export type Renderer = z.infer<typeof RendererSchema>;

/** `#rrggbb` or `#rrggbbaa`, any case. */
export const PaletteColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, 'must be #rrggbb or #rrggbbaa');
export type PaletteColor = z.infer<typeof PaletteColorSchema>;

/** A role id: camelCase, e.g. `base`, `textMuted`. */
export const PaletteRoleIdSchema = z.string().regex(/^[a-z][a-zA-Z0-9]{0,31}$/, 'must be a camelCase role id');

/** role → colour. */
export const PaletteColorsSchema = z.record(PaletteRoleIdSchema, PaletteColorSchema);
export type PaletteColors = z.infer<typeof PaletteColorsSchema>;

/** Most roles one bundle declares. */
export const MAX_PALETTE_ROLES = 16;

/**
 * What each renderer needs: the roles its code draws with, and the foreground/background
 * pairs whose contrast is checked (a low ratio is a preflight warning, not a block).
 */
export const RENDERER_PALETTE: Readonly<Record<Renderer, { required: readonly string[]; contrastPairs: readonly (readonly [fg: string, bg: string])[] }>> = {
  dom: {
    required: ['background', 'surface', 'text', 'textMuted', 'accent', 'accentText', 'frame'],
    contrastPairs: [
      ['text', 'surface'],
      ['textMuted', 'surface'],
      ['accentText', 'accent'],
    ],
  },
  signal: {
    required: ['base', 'surface', 'line', 'text', 'textMuted', 'signal', 'signalText', 'info', 'alert', 'warning'],
    contrastPairs: [
      ['text', 'base'],
      ['text', 'surface'],
      ['textMuted', 'base'],
      ['signalText', 'signal'],
    ],
  },
};

/** Minimum contrast for the declared pairs (WCAG AA for normal text). */
export const MIN_CONTRAST = 4.5;

/** A bundle's palette declaration (in `tokens.json`). */
export const PaletteSchema = z
  .strictObject({
    /** Every role the bundle's renderer draws with, with a label for the admin editor. */
    roles: z.array(z.strictObject({ id: PaletteRoleIdSchema, label: z.string().trim().min(1).max(60) })).min(1).max(MAX_PALETTE_ROLES),
    /** One colour per role. */
    default: PaletteColorsSchema,
    /** Named alternatives; each sets every role. */
    presets: z.array(z.strictObject({ name: z.string().trim().min(1).max(60), colors: PaletteColorsSchema })).max(12),
  })
  .superRefine((p, ctx) => {
    const ids = p.roles.map((r) => r.id);
    const dup = ids.find((id, i) => ids.indexOf(id) !== i);
    if (dup) ctx.addIssue({ code: 'custom', path: ['roles'], message: `role "${dup}" is declared twice` });
    const exact = (colors: PaletteColors, path: (string | number)[]) => {
      for (const id of ids) if (!(id in colors)) ctx.addIssue({ code: 'custom', path, message: `missing role "${id}"` });
      for (const id of Object.keys(colors)) if (!ids.includes(id)) ctx.addIssue({ code: 'custom', path, message: `unknown role "${id}"` });
    };
    exact(p.default, ['default']);
    p.presets.forEach((preset, i) => exact(preset.colors, ['presets', i, 'colors']));
    const names = p.presets.map((x) => x.name);
    const dupName = names.find((n, i) => names.indexOf(n) !== i);
    if (dupName) ctx.addIssue({ code: 'custom', path: ['presets'], message: `preset "${dupName}" is declared twice` });
  });
export type Palette = z.infer<typeof PaletteSchema>;

/** Roles the renderer needs that the bundle does not declare. */
export function missingRendererRoles(renderer: Renderer, palette: Palette): string[] {
  const ids = new Set(palette.roles.map((r) => r.id));
  return RENDERER_PALETTE[renderer].required.filter((id) => !ids.has(id));
}

/** `#rrggbb[aa]` → channels 0–255 and alpha 0–1. */
export function parsePaletteColor(color: string): { r: number; g: number; b: number; a: number } {
  const hex = color.slice(1);
  const n = (i: number) => parseInt(hex.slice(i, i + 2), 16);
  return { r: n(0), g: n(2), b: n(4), a: hex.length === 8 ? n(6) / 255 : 1 };
}

/** `#rrggbb[aa]` → `0xrrggbb` (a PixiJS tint; alpha is applied separately). */
export function paletteTint(color: string): number {
  return parseInt(color.slice(1, 7), 16);
}

/** WCAG relative luminance (alpha ignored: pairs are judged as opaque colours). */
export function relativeLuminance(color: string): number {
  const { r, g, b } = parsePaletteColor(color);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** The palette the surfaces draw with: bundle default, overlaid with the project override. */
export interface EffectivePalette {
  colors: PaletteColors;
  /** Roles the override sets that the bundle no longer declares (ignored; preflight warns). */
  unknownOverrideRoles: string[];
}

export function effectivePalette(palette: Palette, override: PaletteColors | undefined): EffectivePalette {
  const colors: PaletteColors = { ...palette.default };
  const unknownOverrideRoles: string[] = [];
  for (const [role, color] of Object.entries(override ?? {})) {
    if (role in colors) colors[role] = color;
    else unknownOverrideRoles.push(role);
  }
  return { colors, unknownOverrideRoles };
}

/** A declared pair below {@link MIN_CONTRAST}. */
export interface ContrastWarning {
  fg: string;
  bg: string;
  ratio: number;
}

export function contrastWarnings(renderer: Renderer, colors: PaletteColors): ContrastWarning[] {
  const out: ContrastWarning[] = [];
  for (const [fg, bg] of RENDERER_PALETTE[renderer].contrastPairs) {
    const a = colors[fg];
    const b = colors[bg];
    if (!a || !b) continue;
    const ratio = Math.round(contrastRatio(a, b) * 100) / 100;
    if (ratio < MIN_CONTRAST) out.push({ fg, bg, ratio });
  }
  return out;
}
